import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface ModalProps {
  is_open: boolean;
  on_close: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  show_close_button?: boolean;
  close_on_overlay_click?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  is_open,
  on_close,
  title,
  children,
  footer,
  size = 'md',
  show_close_button = true,
  close_on_overlay_click = true
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (is_open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [is_open]);
  
  // Close on ESC key
  useEffect(() => {
    const handle_escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && is_open) {
        on_close();
      }
    };
    
    document.addEventListener('keydown', handle_escape);
    return () => document.removeEventListener('keydown', handle_escape);
  }, [is_open, on_close]);
  
  const size_classes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-7xl mx-4'
  };
  
  const handle_overlay_click = () => {
    if (close_on_overlay_click) {
      on_close();
    }
  };
  
  return (
    <AnimatePresence>
      {is_open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={handle_overlay_click}
          />
          
          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className={`relative w-full ${size_classes[size]} max-h-[90vh] flex flex-col bg-white rounded-xl shadow-2xl`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            {(title || show_close_button) && (
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                {title && (
                  <h2 className="text-xl font-semibold text-charcoal">
                    {title}
                  </h2>
                )}
                {show_close_button && (
                  <button
                    onClick={on_close}
                    className="p-1 rounded-lg text-gray-400 hover:text-charcoal hover:bg-gray-100 transition-smooth"
                    aria-label="Close modal"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            )}
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {children}
            </div>
            
            {/* Footer */}
            {footer && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};